import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    doc,
    collection,
    onSnapshot,
    updateDoc,
    addDoc,
    query,
    orderBy,
    serverTimestamp,
    increment,
} from 'firebase/firestore';
import type { Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './BizMessages.css';

interface BizMessageSummary {
    displayName: string;
    email?: string;
    lastMessage: string;
    lastMessageAt: Timestamp;
    status: 'open' | 'closed';
    unreadByAdmin: number | boolean;
}

interface ThreadMessage {
    id: string;
    content: string;
    fromAdmin: boolean;
    senderName: string;
    createdAt: Timestamp;
    read: boolean;
}

export const BizMessageDetail: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();

    const [summary, setSummary] = useState<BizMessageSummary | null>(null);
    const [messages, setMessages] = useState<ThreadMessage[]>([]);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const bottomRef = useRef<HTMLDivElement>(null);
    const markedUnread = useRef(false);

    useEffect(() => {
        if (!userId) return;

        const summaryUnsub = onSnapshot(doc(db, 'bizMessages', userId), (snap) => {
            if (!snap.exists()) return;
            const data = snap.data() as BizMessageSummary;
            setSummary(data);

            if (data.unreadByAdmin && !markedUnread.current) {
                markedUnread.current = true;
                updateDoc(doc(db, 'bizMessages', userId), { unreadByAdmin: 0 }).catch(
                    (err) => console.error('Failed to mark read:', err)
                );
            }
        });

        const threadUnsub = onSnapshot(
            query(
                collection(db, 'bizMessages', userId, 'thread'),
                orderBy('createdAt', 'asc')
            ),
            (snap) => {
                const loaded: ThreadMessage[] = snap.docs.map((d) => ({
                    id: d.id,
                    ...(d.data() as Omit<ThreadMessage, 'id'>),
                }));
                setMessages(loaded);
            }
        );

        return () => {
            summaryUnsub();
            threadUnsub();
        };
    }, [userId]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async () => {
        if (!replyText.trim() || !userId || !currentUser) return;

        const content = replyText.trim();
        setSending(true);
        setError('');

        try {
            await addDoc(collection(db, 'bizMessages', userId, 'thread'), {
                content,
                fromAdmin: true,
                senderName: currentUser.email ?? 'Admin',
                createdAt: serverTimestamp(),
                read: false,
            });
            await updateDoc(doc(db, 'bizMessages', userId), {
                lastMessage: content,
                lastMessageAt: serverTimestamp(),
                unreadByAdmin: 0,
                unreadByUser: increment(1),
            });
            setReplyText('');
        } catch (err: any) {
            setError(err.message || 'Failed to send reply');
        } finally {
            setSending(false);
        }
    };

    const handleStatusToggle = async () => {
        if (!userId || !summary) return;
        const newStatus = summary.status === 'open' ? 'closed' : 'open';
        try {
            await updateDoc(doc(db, 'bizMessages', userId), { status: newStatus });
        } catch (err: any) {
            setError(err.message || 'Failed to update status');
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (err) {
            console.error('Sign out failed:', err);
        }
    };

    const formatTime = (ts: Timestamp | undefined) => {
        if (!ts?.toDate) return '';
        return ts.toDate().toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    return (
        <div className="biz-detail-container">
            <header className="biz-detail-header">
                <div className="biz-detail-header-content">
                    <div className="biz-detail-header-left">
                        <button
                            onClick={() => navigate('/biz-messages')}
                            className="biz-detail-back-button"
                        >
                            ← Back
                        </button>
                        <div>
                            <h1 className="biz-detail-title">
                                {summary?.displayName ?? userId}
                            </h1>
                            {summary?.email && (
                                <p className="biz-detail-email">{summary.email}</p>
                            )}
                        </div>
                        {summary && (
                            <span className={`biz-status-badge biz-status-${summary.status}`}>
                                {summary.status}
                            </span>
                        )}
                    </div>
                    <div className="biz-detail-header-right">
                        {summary && (
                            <button
                                onClick={handleStatusToggle}
                                className={`biz-detail-status-btn ${summary.status === 'open' ? 'close' : 'reopen'}`}
                            >
                                {summary.status === 'open' ? 'Close Thread' : 'Reopen Thread'}
                            </button>
                        )}
                        <span className="biz-msgs-user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="biz-msgs-logout-button">
                            Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <main className="biz-detail-main">
                {error && (
                    <div className="biz-detail-error">⚠️ {error}</div>
                )}

                <div className="biz-chat-thread">
                    {messages.length === 0 && (
                        <div className="biz-chat-empty">
                            <p>No messages yet.</p>
                        </div>
                    )}
                    {messages.map((msg) => (
                        <div
                            key={msg.id}
                            className={`biz-bubble-wrap ${msg.fromAdmin ? 'admin' : 'user'}`}
                        >
                            <div className={`biz-bubble ${msg.fromAdmin ? 'biz-bubble-admin' : 'biz-bubble-user'}`}>
                                <p className="biz-bubble-text">{msg.content}</p>
                                <div className="biz-bubble-meta">
                                    <span className="biz-bubble-sender">
                                        {msg.fromAdmin
                                            ? (msg.senderName ?? 'Admin')
                                            : summary?.displayName ?? 'User'}
                                    </span>
                                    <span className="biz-bubble-time">{formatTime(msg.createdAt)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                    <div ref={bottomRef} />
                </div>

                <div className="biz-reply-form">
                    <textarea
                        className="biz-reply-textarea"
                        placeholder="Write a reply..."
                        rows={3}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.metaKey) handleSend();
                        }}
                    />
                    <button
                        className="biz-reply-send-button"
                        onClick={handleSend}
                        disabled={sending || !replyText.trim()}
                    >
                        {sending ? 'Sending...' : 'Send'}
                    </button>
                </div>
            </main>
        </div>
    );
};

export default BizMessageDetail;
